import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import { SetNameSchema } from "@nexus/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { AdPlatformIcon } from "@/features/opportunities/components/AdPlatformIcon.js";
import { adPlatformLabel } from "@/lib/ad-platform-labels.js";
import {
  CreateSetDateRangePicker,
  PERSONA_EXTRA_ROLLING_PRESET,
} from "@/features/opportunities/components/sets/CreateSetDateRangePicker/CreateSetDateRangePicker";
import {
  CreateSetDialog,
  type CreateSetFormValues,
} from "@/features/opportunities/components/sets/CreateSetDialog/CreateSetDialog";
import { MultiTagInputWithSelectAll } from "@/features/opportunities/components/sets/CreateSetDialog/MultiTagInputWithSelectAll";
import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import {
  isIndustryMissingPlatform,
  sortDisabledIndustriesLast,
} from "@/features/opportunities/lib/industry-platform-availability.js";
import { PersonaStatusBadge } from "@/features/personas/components/PersonaStatusBadge/PersonaStatusBadge";
import {
  humanizePersonaSentence,
  humanizePersonaTitle,
  personaInitials,
} from "@/features/personas/lib/persona-format";
import { personaStatusIsAwaitingReview } from "@/features/personas/lib/persona-status";
import type { Persona } from "@/features/personas/types/personas.types";
import { personaOpportunityGenerationInlineError } from "../../../lib/opportunity-generation-request-errors.js";
import {
  adPlatformsQueryOptions,
  competitorOptionsQueryOptions,
  industriesQueryOptions,
  personaOwnBrandQueryOptions,
  personasQueryOptions,
  productsQueryOptions,
  useCreateUserGeneratedPersonaOpportunitySet,
} from "@/lib/queries";
import { toTitleCase } from "@/lib/title-case";
import { createDefaultPersonaOpportunityQueryTuning } from "../../../constants/persona-opportunity-tuning-defaults.js";
import {
  filterPersonasByIndustry,
  isPersonaSelectable,
} from "../../../lib/filter-personas-by-industry.js";
import {
  createDefaultCreatePersonaSetFormValues,
  createDefaultPersonaSetName,
  isFormComplete,
  missingRequirements,
  type CreatePersonaSetRequirement,
} from "../../../lib/persona-opportunity-form.js";
import type {
  CreatePersonaSetFormValues,
  CreatePersonaSetSubmit,
} from "../../../types/persona-opportunities.types.js";
import { usePersonaOpportunityPermissions } from "../../../hooks/usePersonaOpportunityPermissions.js";
import { PersonaQueryTuningFields } from "../PersonaQueryTuningFields/PersonaQueryTuningFields.js";
import { ChoiceCardGroup, type ChoiceCardOption } from "./ChoiceCardGroup.js";
import { CreateSetFormLayout } from "./CreateSetFormLayout.js";
import { CreateSetNameHeading } from "./CreateSetNameHeading.js";
import { useOpportunityGenerationEnabled } from "../../../hooks/useOpportunityGenerationEnabled.js";
import { DriveModeCards, type DriveMode } from "./DriveModeCards.js";
import { findElementIn, resolveDriveModeFocusTarget } from "./drive-mode-focus.js";

const PERSONA_OPPORTUNITIES_SCOPE = "persona-opportunities" as const;
/**
 * Full-strength `muted-foreground` clears AA at ~4.8:1; the previous `/55` blended to
 * roughly #adb1b9 on the light surface, about 2.1:1. These hints carry the industry
 * empty-state and the query-tuning explanation, which are the last things that should
 * fade out. 12px also matches the counter's warning and error states, so the character
 * count no longer resizes as it crosses the limit threshold.
 */
const FIELD_HINT_CLASS = "text-xs leading-snug text-muted-foreground";

const REQUIREMENT_LABEL_KEYS: Record<CreatePersonaSetRequirement, string> = {
  name: "opportunities.setNameLabel",
  industry: "personaOpportunities.createDialog.industryLabel",
  competitors: "personaOpportunities.createDialog.competitorsLabel",
  adPlatform: "opportunities.adPlatformsLabel",
  personas: "personaOpportunities.createDialog.personasLabel",
  dataWindow: "personaOpportunities.createDialog.dateRangeLabel",
  advanced: "personaOpportunities.createDialog.advancedLabel",
};

/** Keyed by the lowercase ids `/mock-data/ad-platforms` serves; an unknown id gets no description. */
const AD_PLATFORM_DESCRIPTION_KEYS: Record<string, { key: string; fallback: string }> = {
  meta: { key: "personaOpportunities.createDialog.adPlatformDescriptions.meta", fallback: "Facebook & Instagram" },
  google: { key: "personaOpportunities.createDialog.adPlatformDescriptions.google", fallback: "Search & YouTube" },
  tiktok: { key: "personaOpportunities.createDialog.adPlatformDescriptions.tiktok", fallback: "TikTok For You feed" },
};

/** Every field `isFormComplete` gates on carries this, not just the name. */
function RequiredMark() {
  return (
    <span className="text-destructive text-sm" aria-hidden>
      *
    </span>
  );
}

/**
 * Approved personas lead each group: they are the vetted picks, and the list already
 * excludes REJECTED, so SUGGESTED is the only status left that still owes a review.
 */
function sortApprovedFirst(personas: readonly Persona[]): Persona[] {
  return [...personas].sort(
    (a, b) =>
      Number(personaStatusIsAwaitingReview(a.status)) -
      Number(personaStatusIsAwaitingReview(b.status)),
  );
}

function toPersonaTagOption(persona: Persona, group: string) {
  const displayTitle = humanizePersonaTitle(persona.title);
  const who = persona.who?.trim();
  const whoLine = who ? humanizePersonaSentence(who) : undefined;
  const motivation = persona.motivations[0]?.trim();
  // What the persona wants separates two rows faster than who they are: `who` opens with
  // shared demographic scaffolding and truncates before it gets specific, while the
  // motivation is short and already distinct. Same `motivations[0]` the persona cards show.
  const driverLine = motivation ? humanizePersonaSentence(motivation) : undefined;
  return {
    id: persona.id,
    label: displayTitle,
    value: persona.id,
    // Approved is the norm here and stays unlabelled, which leaves the whole line for
    // the persona itself; only the unreviewed rows spend a chip.
    description: driverLine ?? whoLine,
    // Full `who` survives on hover; it only leaves the row because it cannot finish there.
    detail: driverLine ? whoLine : undefined,
    badge: personaStatusIsAwaitingReview(persona.status) ? (
      <PersonaStatusBadge status={persona.status} />
    ) : undefined,
    imageUrl: persona.avatarUrl ?? undefined,
    imageFallback: personaInitials(displayTitle),
    group,
  };
}

function validateSetName(raw: string) {
  const result = SetNameSchema.safeParse(raw);
  if (result.success) return { value: result.data, error: null };
  return { value: raw.trim(), error: result.error.issues[0]?.message ?? "Invalid name" };
}

function buildOfferedDriveModes(
  canGenerateCompetitorSet: boolean,
  canCreateSet: boolean,
  canCreateEmptyCustomSet: boolean,
  isPersonaEnabled: boolean,
  isCompetitorEnabled: boolean,
  isCustomEnabled: boolean,
): DriveMode[] {
  return [
    ...(canCreateSet && isPersonaEnabled ? (["personas"] as const) : []),
    ...(canCreateSet && canGenerateCompetitorSet && isCompetitorEnabled
      ? (["competitor"] as const)
      : []),
    ...(canCreateEmptyCustomSet && isCustomEnabled ? (["custom"] as const) : []),
  ];
}

function resolveOfferedDriveMode(initial: DriveMode, offered: DriveMode[]): DriveMode {
  return offered.includes(initial) ? initial : (offered[0] ?? "personas");
}

export interface CreatePersonaSetDialogProps {
  tenantId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (values: CreatePersonaSetSubmit) => void;
  isSubmitting?: boolean;
  initialName?: string;
  canUseQueryTuning?: boolean;
  /** Persona this dialog was opened for (the library CTA) — preselected on open. */
  initialPersonaId?: string;
  /** Storybook / tests — a mode without interacting with the type cards. */
  initialDriveMode?: DriveMode;
  /** Called after a Custom set is created. Generation is not started. */
  onUserGeneratedCreated?: (set: { id: string; name: string }) => void;
  /** Storybook — open Advanced options without a play click. */
  initialAdvancedOpen?: boolean;
}

export function CreatePersonaSetDialog({
  tenantId,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  initialName,
  canUseQueryTuning = false,
  initialPersonaId,
  initialDriveMode = "personas",
  initialAdvancedOpen = false,
  onUserGeneratedCreated,
}: CreatePersonaSetDialogProps) {
  const { t } = useTranslation();
  const { canGenerateCompetitorSet, canCreateSet, canCreateEmptyCustomSet } =
    usePersonaOpportunityPermissions();
  const { isPersonaEnabled, isCompetitorEnabled, isCustomEnabled, isOpportunityTypesReady } =
    useOpportunityGenerationEnabled(tenantId);
  const createUserGeneratedSet = useCreateUserGeneratedPersonaOpportunitySet();
  const offeredModes = isOpportunityTypesReady
    ? buildOfferedDriveModes(
        canGenerateCompetitorSet,
        canCreateSet,
        canCreateEmptyCustomSet,
        isPersonaEnabled,
        isCompetitorEnabled,
        isCustomEnabled,
      )
    : [];
  const offeredModeKey = offeredModes.join(",");
  const didInitForOpenRef = React.useRef(false);
  /** Restored after a mode switch remounts the picker in the other form tree. */
  const driveModeFocusRef = React.useRef<DriveMode | null>(null);
  /** Scopes the focus lookup so a second open dialog with the same ids is ignored. */
  const dialogBodyRef = React.useRef<HTMLDivElement>(null);
  const autoSelectedCompetitorsForRef = React.useRef<string | null>(null);
  const seededPersonaIdRef = React.useRef<string | null>(null);
  const customCreateInFlightRef = React.useRef(false);
  const [isCustomCreating, setIsCustomCreating] = React.useState(false);

  const [form, setForm] = React.useState<CreatePersonaSetFormValues>(() =>
    createDefaultCreatePersonaSetFormValues(),
  );
  const [driveMode, setDriveMode] = React.useState<DriveMode>(() =>
    resolveOfferedDriveMode(
      initialDriveMode,
      buildOfferedDriveModes(
        canGenerateCompetitorSet,
        canCreateSet,
        canCreateEmptyCustomSet,
        isPersonaEnabled,
        isCompetitorEnabled,
        isCustomEnabled,
      ),
    ),
  );
  const [touched, setTouched] = React.useState(false);
  const [advancedOpen, setAdvancedOpen] = React.useState(initialAdvancedOpen);

  // Catalog routes sit behind GENERATE; the empty-Custom-only form never needs them.
  const catalogEnabled = !!open && !!tenantId && canCreateSet;

  React.useEffect(() => {
    if (offeredModes.includes(driveMode)) return;
    const fallback = offeredModes[0];
    if (fallback) setDriveMode(fallback);
  }, [offeredModeKey, driveMode, offeredModes]);

  React.useEffect(() => {
    if (!open) {
      driveModeFocusRef.current = null;
      return;
    }
    const resolved = resolveDriveModeFocusTarget(
      driveModeFocusRef.current,
      driveMode,
      (elementId) => findElementIn(dialogBodyRef.current, elementId),
    );
    driveModeFocusRef.current = resolved.pending;
    resolved.element?.focus();
  }, [driveMode, canGenerateCompetitorSet, canCreateSet, open]);

  const resetCreateUserGeneratedSet = createUserGeneratedSet.reset;
  React.useEffect(() => {
    if (open && driveMode === "custom") return;
    resetCreateUserGeneratedSet();
  }, [open, driveMode, resetCreateUserGeneratedSet]);

  const {
    data: industries = [],
    isLoading: industriesLoading,
    isError: industriesIsError,
    isSuccess: industriesIsSuccess,
    refetch: refetchIndustries,
  } = useQuery({
    ...industriesQueryOptions(PERSONA_OPPORTUNITIES_SCOPE, tenantId),
    enabled: catalogEnabled,
  });

  const {
    data: assignedIndustries = [],
    isLoading: assignedIndustriesLoading,
  } = useQuery({
    ...industriesQueryOptions(PERSONA_OPPORTUNITIES_SCOPE, tenantId, "assigned"),
    enabled: catalogEnabled && offeredModes.includes("competitor"),
  });

  const { data: competitorRows = [], isLoading: competitorsLoading } = useQuery({
    ...competitorOptionsQueryOptions(
      PERSONA_OPPORTUNITIES_SCOPE,
      tenantId,
      form.industry,
      form.adPlatform ? [form.adPlatform] : undefined,
    ),
    enabled: catalogEnabled && !!form.industry && !!form.adPlatform,
  });

  const {
    data: ownBrand,
    isSuccess: ownBrandReady,
    isError: ownBrandIsError,
  } = useQuery({
    ...personaOwnBrandQueryOptions(tenantId),
    enabled: catalogEnabled,
  });

  const ownBrandSlug = ownBrand?.slug ?? null;
  const ownBrandMissing = ownBrandIsError || (ownBrandReady && !ownBrandSlug);
  const ownBrandConfigured = ownBrandReady && Boolean(ownBrandSlug);

  const industriesEmpty =
    industriesIsSuccess && !industriesLoading && industries.length === 0;
  const industriesUnavailable =
    industriesLoading || industriesIsError || industriesEmpty || ownBrandMissing;

  const { data: personasPage, isLoading: personasLoading } = useQuery({
    ...personasQueryOptions(tenantId),
    enabled: catalogEnabled,
  });

  const { data: adPlatforms = [], isLoading: adPlatformsLoading } = useQuery({
    ...adPlatformsQueryOptions(tenantId),
    enabled: catalogEnabled,
  });

  const { data: productRows, isLoading: productOptionsLoading } = useQuery({
    ...productsQueryOptions(PERSONA_OPPORTUNITIES_SCOPE, tenantId),
    enabled: catalogEnabled && driveMode === "competitor",
  });
  const productOptions = React.useMemo(() => {
    if (!Array.isArray(productRows)) return [];
    return productRows.map((product) => ({
      id: product.name,
      value: product.name,
      label: toTitleCase(product.name),
    }));
  }, [productRows]);

  const competitorOptions = React.useMemo(
    () =>
      competitorRows
        .filter((option) => !ownBrandSlug || option.name !== ownBrandSlug)
        .map((option) => ({
          id: option.name,
          // Raw `name` stays the submitted value; label is display-only.
          label: humanizeBrandName(option.name),
          value: option.name,
        })),
    [competitorRows, ownBrandSlug],
  );

  // A stale link — to a persona that was rejected, removed, or belongs to another
  // tenant — seeds nothing rather than putting a row in the form the picker itself
  // would not offer.
  const initialPersona = React.useMemo(() => {
    if (!initialPersonaId) return null;
    const persona = (personasPage?.personas ?? []).find(
      (candidate) => candidate.id === initialPersonaId,
    );
    return persona && isPersonaSelectable(persona) ? persona : null;
  }, [personasPage?.personas, initialPersonaId]);

  // Only industries where this organization is a client reach the select, and a
  // persona's own industry is not guaranteed to be one of them.
  const initialIndustry =
    initialPersona?.industry &&
    industries.some((option) => option.index === initialPersona.industry)
      ? initialPersona.industry
      : null;

  const personaOptions = React.useMemo(() => {
    const personas = filterPersonasByIndustry(personasPage?.personas ?? [], form.industry);
    const core = sortApprovedFirst(personas.filter((persona) => persona.type === "CORE_SUGGESTED"));
    const industry = sortApprovedFirst(
      personas.filter((persona) => persona.type === "INDUSTRY_SUGGESTED"),
    );
    const coreGroup = t("personaOpportunities.createDialog.personasGroupCore");
    const industryGroup = t("personaOpportunities.createDialog.personasGroupIndustry");

    return [
      ...core.map((persona) => toPersonaTagOption(persona, coreGroup)),
      ...industry.map((persona) => toPersonaTagOption(persona, industryGroup)),
    ];
  }, [personasPage?.personas, form.industry, t]);

  React.useEffect(() => {
    if (!open) {
      didInitForOpenRef.current = false;
      autoSelectedCompetitorsForRef.current = null;
      seededPersonaIdRef.current = null;
      driveModeFocusRef.current = null;
      setTouched(false);
      setAdvancedOpen(false);
      setDriveMode("personas");
      return;
    }
    if (didInitForOpenRef.current) return;
    didInitForOpenRef.current = true;
    setDriveMode(resolveOfferedDriveMode(initialDriveMode, offeredModes));
    setAdvancedOpen(initialAdvancedOpen);

    setForm({
      ...createDefaultCreatePersonaSetFormValues(),
      name: initialName ?? createDefaultPersonaSetName(),
      advanced: createDefaultPersonaOpportunityQueryTuning(),
    });
  }, [open, initialName, offeredModes, initialDriveMode, initialAdvancedOpen]);

  // Opened from a persona: land on a form that already has that persona picked, and
  // its industry with it. Waits for the industry list — seeding first would drop the
  // industry, and re-running once it arrived would overwrite a manual choice.
  React.useEffect(() => {
    if (!open || !initialPersona || !industriesIsSuccess) return;
    if (seededPersonaIdRef.current === initialPersona.id) return;
    seededPersonaIdRef.current = initialPersona.id;
    // Without an eligible industry there is nothing to select the persona *within*;
    // `handleIndustryChange` picks it up as soon as the user chooses one.
    if (!initialIndustry) return;
    autoSelectedCompetitorsForRef.current = null;
    setForm((previous) => ({
      ...previous,
      industry: initialIndustry,
      industryCompetitors: [],
      personaIds: [initialPersona.id],
      personaTitlesById: {
        ...previous.personaTitlesById,
        [initialPersona.id]: humanizePersonaTitle(initialPersona.title),
      },
    }));
  }, [open, initialPersona, initialIndustry, industriesIsSuccess]);

  React.useEffect(() => {
    if (!open || adPlatforms.length === 0 || form.adPlatform) return;
    setForm((previous) => ({ ...previous, adPlatform: adPlatforms[0]?.id ?? "" }));
  }, [open, adPlatforms, form.adPlatform]);

  const selectedIndustry = industries.find((option) => option.index === form.industry);
  const isSelectedIndustryUnavailable =
    selectedIndustry !== undefined && isIndustryMissingPlatform(selectedIndustry, form.adPlatform);

  // One path for every way an industry and platform meet: a persona seed, the default
  // platform, a manual switch or a refreshed catalog. Each clears an unavailable industry.
  React.useEffect(() => {
    if (!open || !isSelectedIndustryUnavailable) return;
    autoSelectedCompetitorsForRef.current = null;
    setForm((previous) => ({ ...previous, ...industryChangePatch("") }));
  }, [open, isSelectedIndustryUnavailable]); // eslint-disable-line react-hooks/exhaustive-deps

  // First load for an industry pre-selects every competitor option (same as the
  // opportunities create dialog). Wait for a successful own-brand response so a
  // failed lookup cannot lock the client brand into the selection. Manual edits
  // after that are left alone.
  React.useEffect(() => {
    if (!open || !form.industry || !form.adPlatform || competitorsLoading || !ownBrandReady) return;
    const autoSelectKey = `${form.adPlatform}:${form.industry}`;
    if (autoSelectedCompetitorsForRef.current === autoSelectKey) return;
    autoSelectedCompetitorsForRef.current = autoSelectKey;
    setForm((previous) => ({
      ...previous,
      industryCompetitors: competitorOptions.map((option) => option.value),
    }));
  }, [open, form.industry, form.adPlatform, driveMode, competitorsLoading, competitorOptions, ownBrandReady]);

  // Drop the own brand if it was selected before the slug resolved (race / industry change).
  React.useEffect(() => {
    if (!ownBrandSlug) return;
    setForm((previous) => {
      if (!previous.industryCompetitors.includes(ownBrandSlug)) return previous;
      return {
        ...previous,
        industryCompetitors: previous.industryCompetitors.filter(
          (competitor) => competitor !== ownBrandSlug,
        ),
      };
    });
  }, [ownBrandSlug]);

  const { value: trimmedName, error: nameError } = validateSetName(form.name);
  const showNameError = touched && !!nameError;
  const isBusy = Boolean(isSubmitting) || createUserGeneratedSet.isPending || isCustomCreating;
  const canCreateCustom = !nameError && !isBusy;
  const customCreateError = createUserGeneratedSet.error
    ? personaOpportunityGenerationInlineError(createUserGeneratedSet.error, t)
    : null;
  const missing = missingRequirements({ ...form, name: trimmedName });
  const canGenerate =
    missing.length === 0 &&
    ownBrandConfigured &&
    offeredModes.includes(driveMode) &&
    !isSelectedIndustryUnavailable;
  // Reuses each field's own label so the summary names the control to scroll back to
  // rather than inventing a second vocabulary for the same fields.
  const missingFieldLabels = missing.map((requirement) =>
    t(REQUIREMENT_LABEL_KEYS[requirement]),
  );

  function updateForm(patch: Partial<CreatePersonaSetFormValues>) {
    setForm((previous) => ({ ...previous, ...patch }));
  }

  function handleIndustryChange(industry: string) {
    autoSelectedCompetitorsForRef.current = null;
    updateForm(industryChangePatch(industry));
  }

  function industryChangePatch(industry: string): Partial<CreatePersonaSetFormValues> {
    // The persona the dialog was opened for survives an industry change wherever the
    // new industry still offers it — a core persona is offered under every industry,
    // so switching industries must not silently drop the reason this dialog is open.
    const keptPersonaIds =
      initialPersona && filterPersonasByIndustry([initialPersona], industry).length > 0
        ? [initialPersona.id]
        : [];
    const personaTitlesById = keptPersonaIds.length > 0 && initialPersona
      ? { [initialPersona.id]: humanizePersonaTitle(initialPersona.title) }
      : {};
    return {
      industry,
      industryCompetitors: [],
      personaIds: keptPersonaIds,
      personaTitlesById,
    };
  }

  function handleAdPlatformChange(adPlatform: string) {
    if (adPlatform === form.adPlatform) return;
    autoSelectedCompetitorsForRef.current = null;
    updateForm({ adPlatform, industryCompetitors: [] });
  }

  function handleCustomCreate() {
    setTouched(true);
    const parsed = SetNameSchema.safeParse(form.name);
    if (
      !parsed.success ||
      customCreateInFlightRef.current ||
      createUserGeneratedSet.isPending ||
      isSubmitting ||
      isCustomCreating
    ) {
      return;
    }
    customCreateInFlightRef.current = true;
    setIsCustomCreating(true);
    createUserGeneratedSet.mutate(parsed.data, {
      onSuccess: (set) => {
        onUserGeneratedCreated?.(set);
        onOpenChange?.(false);
      },
      onSettled: () => {
        customCreateInFlightRef.current = false;
        setIsCustomCreating(false);
      },
    });
  }

  function handleGenerate() {
    setTouched(true);
    const nextForm = { ...form, name: trimmedName };
    if (
      !offeredModes.includes("personas") ||
      !isFormComplete(nextForm) ||
      !ownBrandConfigured ||
      isSelectedIndustryUnavailable
    ) {
      return;
    }
    onSubmit?.({ type: "PERSONA", values: nextForm });
  }

  function handleCompetitorSubmit(values: CreateSetFormValues) {
    if (!offeredModes.includes("competitor")) return;
    onSubmit?.({
      type: "COMPETITOR",
      values: {
        ...values,
        name: trimmedName,
        adPlatforms: form.adPlatform || null,
        startDate: form.startDate,
        endDate: form.endDate,
        activePresetId: form.activePresetId,
      },
    });
  }

  function handleToggleAdvanced() {
    const nextOpen = !advancedOpen;
    setAdvancedOpen(nextOpen);
    updateForm({ showAdvanced: nextOpen });
  }

  function handleDriveModeChange(nextMode: DriveMode) {
    if (nextMode === driveMode) return;
    driveModeFocusRef.current = nextMode;
    setDriveMode(nextMode);
    setAdvancedOpen(false);
    autoSelectedCompetitorsForRef.current = null;
    updateForm({ industryCompetitors: [], showAdvanced: false });
  }

  const dialogTitle = t("personaOpportunities.createDialog.title");
  const nameHeading = (
    <CreateSetNameHeading
      name={form.name}
      onNameChange={(next) => {
        updateForm({ name: next });
        if (!touched) setTouched(true);
        // A stale conflict or validation error belongs to the previous name.
        if (driveMode === "custom") resetCreateUserGeneratedSet();
      }}
      error={showNameError ? nameError : null}
      disabled={isBusy}
    />
  );
  const driveModeField = !isOpportunityTypesReady ? (
    <p role="status" className="text-sm text-muted-foreground">
      {t(
        "personaOpportunities.createDialog.loadingOpportunityTypes",
        "Loading opportunity types…",
      )}
    </p>
  ) : offeredModes.length > 1 ? (
    <DriveModeCards
      value={driveMode}
      onChange={handleDriveModeChange}
      disabled={isBusy}
      modes={offeredModes}
    />
  ) : (
    <input type="hidden" name="driveMode" value={offeredModes[0] ?? "personas"} />
  );
  const adPlatformFieldLabel = (
    <span className="inline-flex items-center gap-0.5">
      {t("opportunities.adPlatformsLabel")}
      <RequiredMark />
    </span>
  );
  const adPlatformOptions: ChoiceCardOption<string>[] = adPlatforms.map((platform) => {
    const descriptionKey = AD_PLATFORM_DESCRIPTION_KEYS[platform.id];
    return {
      value: platform.id,
      title: adPlatformLabel(t, platform.id, platform.name),
      icon: <AdPlatformIcon platformId={platform.id} />,
      description: descriptionKey ? t(descriptionKey.key, descriptionKey.fallback) : undefined,
    };
  });
  const adPlatformField =
    adPlatformsLoading || adPlatformOptions.length === 0 ? (
      <div className="flex flex-col gap-2">
        <Label>{adPlatformFieldLabel}</Label>
        <p className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
          {adPlatformsLoading ? (
            <>
              <Icon name="loader" size="xs" color="inherit" className="animate-spin" />
              {t("common.loading")}
            </>
          ) : (
            t("personaOpportunities.createDialog.adPlatformsEmpty", "No ad platforms are available for this organization.")
          )}
        </p>
      </div>
    ) : (
      <ChoiceCardGroup
        id="persona-set-platform"
        label={adPlatformFieldLabel}
        options={adPlatformOptions}
        value={form.adPlatform}
        onChange={handleAdPlatformChange}
        disabled={isSubmitting}
      />
    );
  const scopeRow = (
    <div className="flex flex-col gap-5">
      {adPlatformField}

      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center gap-0.5">
          <Label htmlFor="persona-set-date-range">{t("personaOpportunities.createDialog.dateRangeLabel", "Date range")}</Label>
          <RequiredMark />
        </div>
        <CreateSetDateRangePicker
          id="persona-set-date-range"
          startDate={form.startDate}
          endDate={form.endDate}
          activePresetId={form.activePresetId}
          extraRollingPresets={[PERSONA_EXTRA_ROLLING_PRESET]}
          onChange={(next) => updateForm(next)}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
  const advancedLabel = t("personaOpportunities.createDialog.advancedLabel");
  const personaFooter = (
    <DialogFooter className="shrink-0 sm:items-center">
      {/* A greyed-out Generate is otherwise silent about why. `status` announces the
          remaining fields as they resolve, which a `disabled` button cannot do —
          it is skipped by assistive tech and never receives focus. */}
      {missingFieldLabels.length > 0 ? (
        <p
          role="status"
          data-testid="persona-set-missing"
          className="text-xs text-muted-foreground sm:mr-auto"
        >
          {t("personaOpportunities.createDialog.missingRequirements", {
            fields: missingFieldLabels.join(", "),
          })}
        </p>
      ) : canGenerate ? (
        <p
          role="status"
          data-testid="persona-set-ready"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground sm:mr-auto"
        >
          <Icon name="check-circle" size="xs" color="inherit" className="text-success" />
          {t("personaOpportunities.createDialog.readyToGenerate", "Ready to generate")}
        </p>
      ) : null}
      <Button
        type="button"
        variant="outline"
        onClick={() => onOpenChange?.(false)}
        disabled={isSubmitting}
      >
        {t("common.cancel")}
      </Button>
      <Button
        type="button"
        data-testid="persona-set-generate"
        iconStart="sparkles"
        onClick={handleGenerate}
        disabled={!canGenerate || isSubmitting}
      >
        {isSubmitting ? t("personaOpportunities.createDialog.generating") : t("personaOpportunities.createDialog.generate")}
      </Button>
    </DialogFooter>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => { if (isBusy) return; onOpenChange?.(next); }}>
      <DialogContent
        variant="form"
        className="flex h-[min(90vh,760px)] w-full flex-col gap-0 overflow-hidden sm:max-w-xl"
        aria-describedby={undefined}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader className="-mx-5 -mt-5 shrink-0 flex-row items-start gap-3 border-b border-border px-5 pb-4 pt-5 pr-12">
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-success-subtle text-success-subtle-foreground"
          >
            <Icon name="sparkles" size="md" color="inherit" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <DialogTitle className="text-base font-semibold leading-snug">{dialogTitle}</DialogTitle>
          </div>
        </DialogHeader>

        <div ref={dialogBodyRef} className="flex min-h-0 flex-1 flex-col">
        {driveMode === "custom" ? (
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(event) => {
              event.preventDefault();
              handleCustomCreate();
            }}
          >
            <CreateSetFormLayout
              footer={
                <DialogFooter className="shrink-0 sm:items-center">
                  <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={isBusy}>
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button
                    type="submit"
                    data-testid="persona-set-create-custom"
                    disabled={!canCreateCustom}
                  >
                    {isCustomCreating || createUserGeneratedSet.isPending
                      ? t("common.creating", "Creating…")
                      : t("personaOpportunities.hub.actions.createSet", "Create Set")}
                  </Button>
                </DialogFooter>
              }
              fields={
                <>
                  {nameHeading}
                  {driveModeField}
                  {customCreateError ? (
                    <p role="alert" className="text-xs text-destructive">
                      {customCreateError}
                    </p>
                  ) : null}
                </>
              }
            />
          </form>
        ) : driveMode === "competitor" ? (
          <CreateSetDialog
            embedded
            embeddedLayout={({ form, advanced, footer }) => (
              <CreateSetFormLayout
                fields={
                  <>
                    {nameHeading}
                    {driveModeField}
                    {scopeRow}
                    <div className="flex flex-col gap-5 border-t border-border pt-5">{form}</div>
                  </>
                }
                advanced={{
                  label: advancedLabel,
                  hint: t("opportunities.embeddedAdvancedHint"),
                  open: advancedOpen,
                  onToggle: handleToggleAdvanced,
                  content: advanced,
                }}
                footer={footer}
              />
            )}
            open={open}
            onOpenChange={onOpenChange}
            onSubmit={handleCompetitorSubmit}
            isSubmitting={isSubmitting}
            embeddedName={form.name}
            embeddedAdPlatform={form.adPlatform}
            embeddedDate={{
              startDate: form.startDate,
              endDate: form.endDate,
              activePresetId: form.activePresetId,
            }}
            initialName={form.name}
            industries={assignedIndustries}
            industriesLoading={assignedIndustriesLoading}
            adPlatformOptions={adPlatforms}
            adPlatformOptionsLoading={adPlatformsLoading}
            productOptions={productOptions}
            productOptionsLoading={productOptionsLoading}
            dataScope={PERSONA_OPPORTUNITIES_SCOPE}
            tenantId={tenantId}
          />
        ) : (
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            handleGenerate();
          }}
        >
        <CreateSetFormLayout
          advanced={
            canUseQueryTuning
              ? {
                  label: advancedLabel,
                  hint: t("personaOpportunities.createDialog.advancedHint"),
                  open: advancedOpen,
                  onToggle: handleToggleAdvanced,
                  content: (
                    <PersonaQueryTuningFields
                      value={form.advanced}
                      onChange={(advanced) => updateForm({ advanced })}
                      disabled={isSubmitting}
                    />
                  ),
                }
              : undefined
          }
          footer={personaFooter}
          fields={
        <>
          {nameHeading}
          {driveModeField}
          {scopeRow}

          <div className="flex flex-col gap-5 border-t border-border pt-5">
          {ownBrandMissing ? (
            <p role="alert" className="text-xs text-destructive">
              {t(
                "personaOpportunities.createDialog.clientBrandRequired",
                "The client brand needs to be configured.",
              )}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <Label htmlFor="persona-set-industry">
                  {t("personaOpportunities.createDialog.industryLabel")}
                </Label>
                <RequiredMark />
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
                      aria-label={t(
                        "personaOpportunities.createDialog.industryHelpAria",
                        "About client industries",
                      )}
                    >
                      <Icon name="info" size="xs" color="muted" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-56">
                    {t(
                      "personaOpportunities.createDialog.industryHelp",
                      "Only industries where this organization is a client. Assigned industries without the client are hidden.",
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={form.industry}
              onValueChange={handleIndustryChange}
              disabled={industriesUnavailable || !form.adPlatform}
            >
              <SelectTrigger id="persona-set-industry" aria-busy={industriesLoading || undefined}>
                {industriesLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="loader" size="xs" className="animate-spin" />
                    {t("common.loading")}
                  </span>
                ) : (
                  <SelectValue placeholder={t("personaOpportunities.createDialog.industryPlaceholder")} />
                )}
              </SelectTrigger>
              <SelectContent>
                {sortDisabledIndustriesLast(industries, (option) =>
                  isIndustryMissingPlatform(option, form.adPlatform),
                ).map((option) => {
                  const isMissingPlatform = isIndustryMissingPlatform(option, form.adPlatform);
                  return (
                    <SelectItem key={option.index} value={option.index} disabled={isMissingPlatform}>
                      {option.title}
                      {isMissingPlatform ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {t("opportunities.industryNoPlatformDataLabel")}
                        </span>
                      ) : null}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {industriesIsError ? (
              <div className="flex flex-col gap-1.5" role="alert">
                <p className="text-xs text-destructive">
                  {t(
                    "personaOpportunities.createDialog.industryLoadError",
                    "Couldn’t load eligible industries. Try again.",
                  )}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={() => {
                    void refetchIndustries();
                  }}
                >
                  {t("common.retry", "Retry")}
                </Button>
              </div>
            ) : null}
            {!form.adPlatform ? (
              <p className={FIELD_HINT_CLASS}>
                {t("personaOpportunities.createDialog.platformFirstHint", "Select an ad platform first.")}
              </p>
            ) : industriesEmpty ? (
              <p className={FIELD_HINT_CLASS} role="status">
                {t(
                  "personaOpportunities.createDialog.industryEmptyEligible",
                  "No eligible industries for this organization. The client must appear in an industry before you can create a set.",
                )}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="persona-set-competitors">{t("personaOpportunities.createDialog.competitorsLabel")}</Label>
              <RequiredMark />
            </div>
            <MultiTagInputWithSelectAll
              id="persona-set-competitors"
              options={competitorOptions}
              value={form.industryCompetitors}
              onChange={(industryCompetitors) => updateForm({ industryCompetitors })}
              disabled={!form.adPlatform || !form.industry || competitorsLoading}
              disableFreeText
              renderTag={humanizeBrandName}
              placeholder={t("personaOpportunities.createDialog.competitorsPlaceholder")}
              noOptionsText={t("personaOpportunities.createDialog.competitorsNoOptions")}
              selectedOfTotalLabel={(selected, total) =>
                t("personaOpportunities.createDialog.competitorsSelectedOfTotal", { selected, total })}
              noMatchText={(query) => t("personaOpportunities.createDialog.competitorsNoMatch", { query })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="persona-set-personas">{t("personaOpportunities.createDialog.personasLabel")}</Label>
              <RequiredMark />
            </div>
            <MultiTagInputWithSelectAll
              id="persona-set-personas"
              options={personaOptions}
              value={form.personaIds}
              onChange={(personaIds) => {
                const personaTitlesById = { ...form.personaTitlesById };
                for (const id of personaIds) {
                  if (!personaTitlesById[id]) {
                    const option = personaOptions.find((candidate) => candidate.value === id);
                    if (option) personaTitlesById[id] = option.label;
                  }
                }
                for (const id of Object.keys(personaTitlesById)) {
                  if (!personaIds.includes(id)) delete personaTitlesById[id];
                }
                updateForm({ personaIds, personaTitlesById });
              }}
              disabled={!form.adPlatform || !form.industry || personasLoading}
              disableFreeText
              placeholder={t("personaOpportunities.createDialog.personasPlaceholder")}
              noOptionsText={t("personaOpportunities.createDialog.personasNoOptions")}
              selectedOfTotalLabel={(selected, total) =>
                t("personaOpportunities.createDialog.personasSelectedOfTotal", { selected, total })}
              noMatchText={(query) => t("personaOpportunities.createDialog.personasNoMatch", { query })}
            />
            {!form.adPlatform ? (
              <p className={FIELD_HINT_CLASS}>
                {t("personaOpportunities.createDialog.platformFirstHint", "Select an ad platform first.")}
              </p>
            ) : !form.industry ? (
              <p className={FIELD_HINT_CLASS}>{t("personaOpportunities.createDialog.personasIndustryHint")}</p>
            ) : null}
          </div>
          </div>
        </>
          }
        />
        </form>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
